// TAS-274: the wine-reveal step in TV mode (Mel, 2026-10-02).
//
// The big screen counts down, plays the reveal film and settles on the 3D
// bottle. Every phone — the GM's too — follows along on "Look at the TV" until
// the TV signals `wine-revealed` (when the film ends), then lands straight on
// the finished reveal (WineRevealNew skips its own countdown and film in TV
// mode), 3D bottle included. The bottle preloads while the phone waits.
//
// Safety: if the TV never says so (switched off, asleep, lost its connection),
// every phone releases itself FALLBACK_AFTER_MS after the TV should have
// finished, so a dead TV can't strand the room — the GM in particular needs
// their Continue back.
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGameFlow } from '../../contexts/GameFlowProvider';
import { useTvWineRevealed } from '../../hooks/useTvWineRevealed';
import { preloadBottle } from '../../lib/bottlePreload';
import { reprobeIfBelowFull } from '@tasted/utils/netTier';
import { useNetTier } from '../../hooks/useNetTier';
import LookAtScreen from './LookAtScreen';

// TV: 5 s countdown + a reveal film (about 5 s), with a beat of slack.
export const TV_REVEAL_EXPECTED_MS = 11_000;
// Mel: "fall back to the final reveal after 7 seconds if it gets stuck".
export const FALLBACK_AFTER_MS = 7_000;

export default function WineRevealGate({ children }) {
  const { gameViewMode, currentWineId, stepEnteredAtMs } = useGameFlow();
  const [searchParams] = useSearchParams();
  // No wine yet (a failed fetch on reload): don't hold — the page's own
  // resync and "Skip ahead" recovery must be reachable (Codex, 2026-10-02).
  const held = gameViewMode === 'tv' && Boolean(currentWineId);
  const revealed = useTvWineRevealed(searchParams.get('code'), currentWineId, { enabled: held });
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    setTimedOut(false);
    if (!held || !currentWineId) return undefined;
    const start = Number.isFinite(stepEnteredAtMs) ? stepEnteredAtMs : Date.now();
    const wait = Math.max(0, start + TV_REVEAL_EXPECTED_MS + FALLBACK_AFTER_MS - Date.now());
    const t = setTimeout(() => setTimedOut(true), wait);
    return () => clearTimeout(t);
  }, [held, currentWineId, stepEnteredAtMs]);

  const waiting = held && !revealed && !timedOut;
  // Subscribed: a connection re-measured as full while the TV counts down
  // should start its download now, not when the reveal opens (Codex).
  const netTier = useNetTier();

  // Warm the 3D bottle while the TV counts down. Released the moment the page
  // takes over: it fetches the same files (now in the HTTP cache) itself.
  useEffect(() => {
    if (!waiting || !currentWineId) return undefined;
    reprobeIfBelowFull();
    const pre = preloadBottle(currentWineId);
    return () => pre?.cancel?.();
    // netTier is what changes preloadBottle's answer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiting, currentWineId, netTier]);

  if (waiting) {
    return <LookAtScreen title="Follow along on the TV" subtitle="The wine is being revealed on the big screen" />;
  }
  return children;
}
